import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HelcimPayScriptLoaderService } from './helcimpay-script-loader.service';

export interface HelcimPayModalResult {
  eventName?: string;
  eventStatus?: boolean;
  status: string;
  transactionId?: string;
  cardToken?: string;
  hash?: string;
  amount?: number;
  currency?: string;
  innerDataJson?: string;
  raw: unknown;
}

type HelcimWindow = Window & {
  appendHelcimPayIframe?: (checkoutToken: string) => void;
  removeHelcimPayIframe?: () => void;
};

@Injectable({
  providedIn: 'root'
})
export class HelcimPayModalService {
  // eslint-disable-next-line @angular-eslint/prefer-inject
  constructor(private readonly scriptLoader: HelcimPayScriptLoaderService) {}

  async openModal(checkoutToken: string): Promise<HelcimPayModalResult> {
    if (!checkoutToken?.trim()) {
      throw new Error('Checkout token is required.');
    }

    await this.scriptLoader.load();

    const globalWindow = window as HelcimWindow;
    const appendIframe = globalWindow.appendHelcimPayIframe;
    if (typeof appendIframe !== 'function') {
      throw new Error('HelcimPay script loaded but modal launcher is unavailable.');
    }

    const allowedOrigins = new Set(environment.helcimPay.allowedOrigins.map(origin => origin.toLowerCase()));

    return await new Promise<HelcimPayModalResult>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        cleanup();
        reject(new Error('HelcimPay modal timed out waiting for a response.'));
      }, environment.helcimPay.modalTimeoutMs);

      const messageHandler = (event: MessageEvent): void => {
        if (!allowedOrigins.has(event.origin.toLowerCase())) {
          return;
        }

        const payload = extractHelcimPayload(event.data);
        if (!payload) {
          return;
        }

        const hasCompletionFields = !!payload.transactionId && !!payload.hash;
        const hasTerminalStatus = isTerminalStatus(payload.status);
        const hasCompletionEvent = isCompletionEvent(payload.eventName, payload.eventStatus);

        if (!hasCompletionFields && !hasTerminalStatus && !hasCompletionEvent) {
          return;
        }

        if (!hasCompletionFields && !hasCompletionEvent && isLikelyHttpStatus(payload.status)) {
          return;
        }

        cleanup();

        if (typeof globalWindow.removeHelcimPayIframe === 'function') {
          globalWindow.removeHelcimPayIframe();
        }

        resolve({
          eventName: payload.eventName,
          eventStatus: payload.eventStatus,
          status: payload.status,
          transactionId: payload.transactionId,
          cardToken: payload.cardToken,
          hash: payload.hash,
          amount: payload.amount,
          currency: payload.currency,
          innerDataJson: payload.innerDataJson,
          raw: event.data
        });
      };

      const cleanup = (): void => {
        window.clearTimeout(timeoutId);
        window.removeEventListener('message', messageHandler);
      };

      window.addEventListener('message', messageHandler);

      try {
        appendIframe(checkoutToken);
      } catch (error) {
        cleanup();
        reject(error instanceof Error ? error : new Error('Failed to launch HelcimPay modal.'));
      }
    });
  }
}

function extractHelcimPayload(data: unknown): HelcimPayModalResult | null {
  const normalized = normalizeValue(data);
  const parsed = extractFromAnyDepth(normalized);
  if (!parsed) {
    return null;
  }

  return {
    ...parsed,
    raw: data
  };
}

function extractFromAnyDepth(data: unknown): HelcimPayModalResult | null {
  const eventName = findFirstStringByKeys(data, ['eventName', 'event_name', 'name']);
  const eventStatus = findFirstBooleanByKeys(data, ['eventStatus', 'event_status', 'success']);

  const eventMessage = findFirstStringByKeys(data, ['eventMessage', 'event_message', 'message']);
  const eventMessageObject = asRecord(normalizeValue(eventMessage));
  const nestedRoot = asRecord(eventMessageObject?.['data']);
  const nestedInner = asRecord(nestedRoot?.['data']);
  const innerDataJson = nestedInner ? stableJsonStringify(nestedInner) : undefined;

  const explicitTransactionId = findFirstStringByKeys(data, ['transactionId', 'transactionID', 'transaction_id']);
  const explicitHash = findFirstStringByKeys(data, ['hash', 'responseHash', 'response_hash']);
  const genericTransactionId = findFirstStringByPredicate(
    data,
    (key: string, value: string) => key.toLowerCase().includes('transaction') && isLikelyTransactionId(value)
  );
  const genericHash = findFirstStringByPredicate(
    data,
    (key: string, value: string) => key.toLowerCase().includes('hash') && isLikelySha256(value)
  );

  const transactionId =
    getOptionalString(nestedInner ?? {}, 'transactionId')
    ?? getOptionalString(nestedRoot ?? {}, 'transactionId')
    ?? explicitTransactionId
    ?? genericTransactionId;

  const hash =
    getOptionalString(nestedRoot ?? {}, 'hash')
    ?? getOptionalString(nestedInner ?? {}, 'hash')
    ?? explicitHash
    ?? genericHash;
  const status =
    getOptionalString(nestedInner ?? {}, 'status')
    ?? getOptionalString(nestedRoot ?? {}, 'status')
    ?? findFirstStringByKeys(data, ['status', 'state', 'result'])
    ?? 'UNKNOWN';

  const amount =
    toNumber(nestedInner?.['amount'])
    ?? toNumber(nestedRoot?.['amount'])
    ?? findFirstNumberByKeys(data, ['amount', 'totalAmount', 'paymentAmount', 'amountPaid']);

  const currency =
    getOptionalString(nestedInner ?? {}, 'currency')
    ?? getOptionalString(nestedRoot ?? {}, 'currency')
    ?? findFirstStringByKeys(data, ['currency', 'currencyCode']);

  if (!transactionId || !hash || !isLikelySha256(hash)) {
    return null;
  }

  const cardToken = findFirstStringByKeys(data, ['cardToken', 'card_token']);
  const approvalCode = getOptionalString(nestedInner ?? {}, 'approvalCode');
  const dateCreated = getOptionalString(nestedInner ?? {}, 'dateCreated');
  const customerCode = getOptionalString(nestedInner ?? {}, 'customerCode');

  return {
    eventName,
    eventStatus,
    status,
    transactionId,
    cardToken: cardToken ?? getOptionalString(nestedInner ?? {}, 'cardToken'),
    hash,
    amount,
    currency,
    innerDataJson,
    raw: {
      original: data,
      approvalCode,
      dateCreated,
      customerCode
    }
  };
}

function stableJsonStringify(value: Record<string, unknown>): string | undefined {
  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
}

function findFirstBooleanByKeys(value: unknown, keys: string[]): boolean | undefined {
  const queue: unknown[] = [normalizeValue(value)];
  const visited = new Set<unknown>();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== 'object' || visited.has(current)) {
      continue;
    }

    visited.add(current);

    if (Array.isArray(current)) {
      for (const item of current) {
        queue.push(normalizeValue(item));
      }
      continue;
    }

    const record = current as Record<string, unknown>;
    for (const key of keys) {
      const raw = record[key];
      if (typeof raw === 'boolean') {
        return raw;
      }

      if (typeof raw === 'string') {
        const normalized = raw.trim().toLowerCase();
        if (normalized === 'true') return true;
        if (normalized === 'false') return false;
      }
    }

    for (const child of Object.values(record)) {
      queue.push(normalizeValue(child));
    }
  }

  return undefined;
}

function findFirstStringByPredicate(
  value: unknown,
  predicate: (key: string, normalizedValue: string) => boolean): string | undefined {
  const queue: unknown[] = [normalizeValue(value)];
  const visited = new Set<unknown>();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== 'object' || visited.has(current)) {
      continue;
    }

    visited.add(current);

    if (Array.isArray(current)) {
      for (const item of current) {
        queue.push(normalizeValue(item));
      }
      continue;
    }

    const record = current as Record<string, unknown>;
    for (const [key, child] of Object.entries(record)) {
      const valueAsString = toNonEmptyString(child);
      if (valueAsString && predicate(key, valueAsString)) {
        return valueAsString;
      }

      queue.push(normalizeValue(child));
    }
  }

  return undefined;
}

function getFirstString(data: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = getOptionalString(data, key);
    if (value) {
      return value;
    }
  }

  return undefined;
}

function findFirstNumberByKeys(value: unknown, keys: string[]): number | undefined {
  const queue: unknown[] = [normalizeValue(value)];
  const visited = new Set<unknown>();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== 'object' || visited.has(current)) {
      continue;
    }

    visited.add(current);

    if (Array.isArray(current)) {
      for (const item of current) {
        queue.push(normalizeValue(item));
      }
      continue;
    }

    const record = current as Record<string, unknown>;
    for (const key of keys) {
      const candidate = toNumber(record[key]);
      if (candidate !== undefined) {
        return candidate;
      }
    }

    for (const child of Object.values(record)) {
      queue.push(normalizeValue(child));
    }
  }

  return undefined;
}

function toNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? value as Record<string, unknown> : null;
}

function normalizeValue(value: unknown): unknown {
  if (typeof value !== 'string') {
    return value;
  }

  const text = value.trim();
  if (!text.startsWith('{') && !text.startsWith('[')) {
    return value;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return value;
  }
}

function findFirstStringByKeys(value: unknown, keys: string[]): string | undefined {
  const queue: unknown[] = [normalizeValue(value)];
  const visited = new Set<unknown>();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== 'object' || visited.has(current)) {
      continue;
    }

    visited.add(current);

    if (Array.isArray(current)) {
      for (const item of current) {
        queue.push(normalizeValue(item));
      }
      continue;
    }

    const record = current as Record<string, unknown>;
    const found = getFirstString(record, keys);
    if (found) {
      return found;
    }

    for (const child of Object.values(record)) {
      queue.push(normalizeValue(child));
    }
  }

  return undefined;
}

function getOptionalString(data: Record<string, unknown>, key: string): string | undefined {
  const value = data[key];
  return toNonEmptyString(value);
}

function toNonEmptyString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value.trim().length > 0 ? value : undefined;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    const text = String(value).trim();
    return text.length > 0 ? text : undefined;
  }

  return undefined;
}

function isTerminalStatus(status: string | undefined): boolean {
  const normalized = (status ?? '').toLowerCase();
  return normalized.includes('approved')
    || normalized.includes('success')
    || normalized.includes('complete')
    || normalized.includes('paid')
    || normalized.includes('declined')
    || normalized.includes('failed')
    || normalized.includes('error')
    || normalized.includes('cancel');
}

function isLikelySha256(value: string): boolean {
  const normalized = value.trim();
  return /^[A-Fa-f0-9]{64}$/.test(normalized);
}

function isLikelyTransactionId(value: string): boolean {
  const normalized = value.trim();
  if (normalized.length < 6 || normalized.length > 80) {
    return false;
  }

  return /^[A-Za-z0-9_-]+$/.test(normalized);
}

function isLikelyHttpStatus(status: string | undefined): boolean {
  const normalized = (status ?? '').trim();
  return /^\d{3}$/.test(normalized);
}

function isCompletionEvent(eventName: string | undefined, eventStatus: boolean | undefined): boolean {
  const normalized = (eventName ?? '').toLowerCase();
  const looksFinal = normalized.includes('complete')
    || normalized.includes('success')
    || normalized.includes('approved')
    || normalized.includes('process-transaction')
    || normalized.includes('finished');

  if (!looksFinal) {
    return false;
  }

  return eventStatus !== false;
}

